import React, { useState, useEffect } from 'react';
import { X, Calendar, Edit2, Save } from 'lucide-react';

const FuelPricingCalendarModal = ({ apiCall, onClose, onSuccess, showError, showSuccess }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const fuelTypes = ['Gasoline 95', 'Gasoline 98', 'Mazout'];

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    loadPrices();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  const loadPrices = async () => {
    try {
      setLoading(true);
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      
      const startDate = formatLocalDate(new Date(year, month, 1));
      const endDate = formatLocalDate(new Date(year, month + 1, 0));
      
      const pricesData = await apiCall(`/gas/pricing/range?startDate=${startDate}&endDate=${endDate}`);
      setPrices(pricesData || []);
    } catch (error) {
      showError('Error loading prices: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getPriceForDateAndType = (date, fuelType) => {
    const dateStr = formatLocalDate(date);
    const price = prices.find(p => {
      const priceDate = p.EffectiveDate.split('T')[0];
      return priceDate === dateStr && p.FuelType === fuelType;
    });
    return price ? price.PricePerLiter : null;
  };

  const handleEdit = (date, fuelType) => {
    const price = getPriceForDateAndType(date, fuelType);
    setEditingCell({ date: formatLocalDate(date), fuelType });
    setEditValue(price ? price.toFixed(3) : '');
  };

  const handleSave = async () => {
    if (!editValue || parseFloat(editValue) <= 0) {
      showError('Please enter a valid price');
      return;
    }

    try {
      setSaving(true);
      await apiCall('/gas/pricing', {
        method: 'POST',
        body: JSON.stringify({
          fuelType: editingCell.fuelType,
          pricePerLiter: parseFloat(editValue),
          effectiveDate: editingCell.date
        })
      });
      
      showSuccess('Price updated successfully');
      setEditingCell(null);
      setEditValue('');
      loadPrices();
    } catch (error) {
      showError(error.message);
    } finally {
      setSaving(false);
    }
  };

const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const days = [];

    for (let i = 1; i <= lastDay; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const changeMonth = (offset) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1));
  };

  const days = getDaysInMonth();
  const today = formatLocalDate(new Date());

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Calendar className="text-blue-600" size={24} />
            Fuel Pricing Calendar
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Month Navigation */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={() => changeMonth(-1)}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold transition-colors"
            >
              ← Previous
            </button>
            <h4 className="text-xl font-bold text-gray-800">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h4>
            <button
              onClick={() => changeMonth(1)}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold transition-colors"
            >
              Next →
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading prices...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold sticky left-0 bg-gray-100 z-10">
                      Date
                    </th>
                    {fuelTypes.map(type => (
                      <th key={type} className="border border-gray-300 px-4 py-2 text-center font-semibold">
                        {type}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {days.map(date => {
                    const dateStr = formatLocalDate(date);
                    const isToday = dateStr === today;
                    const dayOfWeek = date.getDay();
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                    
                    return (
                      <tr 
                        key={dateStr} 
                        className={`${isToday ? 'bg-blue-50' : isWeekend ? 'bg-gray-50' : ''}`}
                      >
                        <td className={`border border-gray-300 px-4 py-2 font-semibold sticky left-0 z-10 ${isToday ? 'bg-blue-50' : isWeekend ? 'bg-gray-50' : 'bg-white'}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-xs w-8">
                              {date.toLocaleDateString('en-US', { weekday: 'short' })}
                            </span>
                            <span>
                              {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                            {isToday && (
                              <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full font-semibold">
                                Today
                              </span>
                            )}
                          </div>
                        </td>
                        {fuelTypes.map(fuelType => {
                          const price = getPriceForDateAndType(date, fuelType);
                          const isEditing = editingCell?.date === dateStr && editingCell?.fuelType === fuelType;
                          
                          return (
                            <td key={fuelType} className="border border-gray-300 px-2 py-2 text-center">
                              {isEditing ? (
                                <div className="flex items-center gap-2 justify-center">
                                  <input
                                    type="number"
                                    step="0.001"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    className="w-24 px-2 py-1 border border-blue-500 rounded focus:ring-2 focus:ring-blue-500 text-center"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSave();
                                      if (e.key === 'Escape') setEditingCell(null);
                                    }}
                                  />
                                  <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="p-1 bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50"
                                    title="Save"
                                  >
                                    <Save size={16} />
                                  </button>
                                  <button
                                    onClick={() => setEditingCell(null)}
                                    className="p-1 bg-gray-400 hover:bg-gray-500 text-white rounded"
                                    title="Cancel"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-center gap-2">
                                  {price !== null ? (
                                    <span className="font-semibold text-green-600">
                                      ${price.toFixed(3)}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400 italic text-sm">—</span>
                                  )}
                                  <button
                                    onClick={() => handleEdit(date, fuelType)}
                                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                                    title="Edit price"
                                  >
                                    <Edit2 size={14} className="text-blue-600" />
                                  </button>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              <p className="font-semibold mb-1">Legend:</p>
              <div className="flex gap-4">
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 bg-blue-50 border border-blue-200 rounded"></span>
                  Today
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 bg-gray-50 border border-gray-200 rounded"></span>
                  Weekend
                </span>
              </div>
            </div>
            <button
              onClick={() => {
                onSuccess();
                onClose();
              }}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FuelPricingCalendarModal;